import { useCallback, useEffect, useId, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link, NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  CloudArrowUpIcon,
  FolderIcon,
  GlobeAltIcon,
  HomeIcon,
  MoonIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  SunIcon,
  TrashIcon
} from "@heroicons/react/24/outline";
import type { ConversionTarget, DocumentCapability, DocumentOperation } from "@xhalo/document-contracts";
import { buildSplitPageGroups, getPdfPageCount, parsePageRanges, validateImageQuality, type SplitMode } from "@xhalo/document-browser-engine";
import { toolByOperation, toolCatalog } from "./tool-catalog";
import { useDocumentStore } from "./store";
import { processLocalPdf } from "./browser-pdf-client";
import { CloudExecution, type CloudJobSettings } from "./cloud-execution";
import { CloudActivityContent, CloudFilesContent } from "./cloud-library";
import { AccountMenu, AuthCallbackContent, AuthEmailContent, SignInContent } from "./auth-ui";
import { documentApiClient, documentMimeForFile } from "./cloud-document-client";

const localeLabels = { "zh-CN": "简体中文", "zh-TW": "繁體中文", en: "English", ja: "日本語" } as const;
const localeCodes = { "zh-CN": "ZH-CN", "zh-TW": "ZH-TW", en: "EN", ja: "JA" } as const;
type Locale = keyof typeof localeLabels;
const quickOperations = ["merge", "compress", "split", "convert", "pdf_to_images", "ocr"] as const satisfies readonly DocumentOperation[];

function IconButton({ label, onClick, children, disabled = false }: { label: string; onClick(): void; children: ReactNode; disabled?: boolean }) {
  return <button className="icon-button" type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}>{children}</button>;
}

function Sidebar() {
  const { t } = useTranslation();
  const primary = [
    { to: "/", label: t("nav.home"), icon: HomeIcon },
    { to: "/files", label: t("nav.files"), icon: FolderIcon },
    { to: "/tools", label: t("nav.tools"), icon: Squares2X2Icon },
    { to: "/activity", label: t("nav.activity"), icon: ClockIcon }
  ];
  return <aside className="sidebar">
    <Link className="brand" to="/" aria-label={t("brand")}>
      <img src="/assets/xhalo-mark.png" alt="" width="28" height="28" />
      <span>{t("brand")}</span>
    </Link>
    <nav className="primary-nav" aria-label="Primary">
      {primary.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}><Icon /><span>{label}</span></NavLink>)}
    </nav>
    <div className="sidebar-footer"><img src="/assets/xhalo-mark.png" alt="" width="22" height="22" /><div><strong>{t("common.copyright")}</strong><span>{t("common.tagline")}</span></div></div>
  </aside>;
}

function Topbar() {
  const { i18n, t } = useTranslation();
  const [dark, setDark] = useState(() => localStorage.getItem("xhalo-document-theme") === "dark");
  const [localeOpen, setLocaleOpen] = useState(false);
  const localeControlRef = useRef<HTMLDivElement>(null);
  const localeMenuRef = useRef<HTMLDivElement>(null);
  const localeMenuId = useId();
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("xhalo-document-theme", dark ? "dark" : "light");
  }, [dark]);
  useEffect(() => {
    if (!localeOpen) return;
    localeMenuRef.current?.querySelector<HTMLButtonElement>("[aria-selected='true']")?.focus();
    const closeOnPointerDown = (event: PointerEvent) => {
      if (!localeControlRef.current?.contains(event.target as Node)) setLocaleOpen(false);
    };
    document.addEventListener("pointerdown", closeOnPointerDown);
    return () => document.removeEventListener("pointerdown", closeOnPointerDown);
  }, [localeOpen]);
  const changeLocale = (locale: Locale) => {
    localStorage.setItem("xhalo-document-locale", locale);
    void i18n.changeLanguage(locale);
    setLocaleOpen(false);
    localeControlRef.current?.querySelector<HTMLButtonElement>(".locale-trigger")?.focus();
  };
  const handleLocaleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const options = [...(localeMenuRef.current?.querySelectorAll<HTMLButtonElement>("[role='option']") ?? [])];
    if (event.key === "Escape") {
      setLocaleOpen(false);
      localeControlRef.current?.querySelector<HTMLButtonElement>(".locale-trigger")?.focus();
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key) || options.length === 0) return;
    event.preventDefault();
    const current = Math.max(0, options.indexOf(event.currentTarget));
    const next = event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 : event.key === "ArrowDown" ? (current + 1) % options.length : (current - 1 + options.length) % options.length;
    options[next]?.focus();
  };
  const activeLocale = (i18n.language in localeLabels ? i18n.language : "zh-CN") as Locale;
  return <header className="topbar">
    <div className="topbar-spacer" />
    <IconButton label={t("header.theme")} onClick={() => setDark((value) => !value)}>{dark ? <MoonIcon /> : <SunIcon />}</IconButton>
    <div className="locale-control" ref={localeControlRef}>
      <button className="locale-trigger" type="button" aria-label={t("header.languageAction")} title={t("header.languageAction")} aria-haspopup="listbox" aria-expanded={localeOpen} aria-controls={localeMenuId} onClick={() => setLocaleOpen((open) => !open)}>
        <GlobeAltIcon aria-hidden="true" /><span>{localeLabels[activeLocale]}</span><ChevronDownIcon className={localeOpen ? "open" : undefined} aria-hidden="true" />
      </button>
      {localeOpen ? <div className="locale-menu" id={localeMenuId} ref={localeMenuRef} role="listbox" aria-label={t("header.languageAction")}>
        <span className="locale-menu-label">{t("header.languageAction")}</span>
        {Object.entries(localeLabels).map(([value, label]) => {
          const locale = value as Locale;
          const selected = locale === activeLocale;
          return <button className="locale-option" type="button" role="option" aria-selected={selected} key={locale} onClick={() => changeLocale(locale)} onKeyDown={handleLocaleKeyDown}>
            <span className="locale-option-name">{label}<small>{localeCodes[locale]}</small></span>{selected ? <CheckIcon aria-hidden="true" /> : <span className="locale-check-placeholder" />}
          </button>;
        })}
      </div> : null}
    </div>
    <span className="topbar-divider" />
    <AccountMenu />
  </header>;
}

function MobileNav() {
  const { t } = useTranslation();
  return <nav className="mobile-nav" aria-label="Mobile">
    <NavLink to="/" end><HomeIcon /><span>{t("nav.home")}</span></NavLink>
    <NavLink to="/files"><FolderIcon /><span>{t("nav.files")}</span></NavLink>
    <NavLink to="/tools"><Squares2X2Icon /><span>{t("nav.tools")}</span></NavLink>
    <NavLink to="/activity"><ClockIcon /><span>{t("nav.activity")}</span></NavLink>
  </nav>;
}

function Shell({ children, rail = true, mobileNav = true }: { children: ReactNode; rail?: boolean; mobileNav?: boolean }) {
  return <div className="app-shell"><Sidebar /><div className="app-body"><Topbar /><main className={rail ? "main-grid" : "main-grid no-rail"}>{children}</main>{mobileNav ? <MobileNav /> : null}</div></div>;
}

function QuickToolCard({ operation }: { operation: DocumentOperation }) {
  const { t } = useTranslation();
  const tool = toolByOperation(operation);
  const Icon = tool.icon;
  return <Link data-testid="quick-tool-card" className="quick-card" to={tool.path} aria-label={`${t(`tools.${operation}.name`)} — ${t(`tools.${operation}.description`)}`}>
    <span className={`tool-icon ${tool.tone}`}><Icon /></span>
    <strong>{t(`tools.${operation}.name`)}</strong>
    <span>{t(`tools.${operation}.description`)}</span>
    <em className={tool.local ? "mode local" : "mode cloud"}>{t(tool.local ? "common.local" : "common.cloud")}</em>
  </Link>;
}

function HomeDropzone() {
  const { t } = useTranslation();
  const inputId = useId();
  const pendingFiles = useDocumentStore((state) => state.pendingFiles);
  const setPendingFiles = useDocumentStore((state) => state.setPendingFiles);
  const accept = (files: FileList | null) => {
    if (!files) return;
    setPendingFiles(Array.from(files).filter((file) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")));
  };
  const drop = (event: DragEvent<HTMLDivElement>) => { event.preventDefault(); accept(event.dataTransfer.files); };
  return <div className="home-dropzone" onDragOver={(event) => event.preventDefault()} onDrop={drop}>
    <CloudArrowUpIcon />
    <div><strong>{t("home.dropTitle")}</strong><span>{pendingFiles.length ? `${pendingFiles.length} PDF` : t("home.dropHint")}</span></div>
    <label className="secondary-button" htmlFor={inputId}>{t("home.choose")}</label>
    <input id={inputId} className="sr-only" type="file" accept="application/pdf,.pdf" multiple onChange={(event) => accept(event.target.files)} />
  </div>;
}

function RightRail() {
  const { t } = useTranslation();
  const groups = [
    { title: t("rail.related"), links: [[t("rail.homepage"), "https://xhalo.co"], [t("rail.blog"), "https://blog.xhalo.co"]] },
    { title: t("rail.help"), links: [[t("rail.docs"), "https://xhalo.co"], [t("rail.faq"), "https://xhalo.co"], [t("rail.feedback"), "https://xhalo.co"]] }
  ];
  return <aside className="right-rail">{groups.map((group) => <section className="rail-card" key={group.title}><h2>{group.title}</h2>{group.links.map(([label, href], index) => <a href={href} key={label} target="_blank" rel="noreferrer"><span className="rail-link-icon">{index === 0 ? <HomeIcon /> : <QuestionMarkCircleIcon />}</span><span>{label}</span><ArrowTopRightOnSquareIcon /></a>)}</section>)}</aside>;
}

function HomePage() {
  const { t } = useTranslation();
  const results = useDocumentStore((state) => state.recentResults);
  return <Shell><div className="content-column">
    <section className="section-heading"><div><h1>{t("home.title")}</h1><p>{t("home.subtitle")}</p></div><Link className="text-link" to="/tools">{t("home.viewAll")}<ArrowTopRightOnSquareIcon /></Link></section>
    <section className="quick-grid">{quickOperations.map((operation) => <QuickToolCard operation={operation} key={operation} />)}</section>
    <HomeDropzone />
    <section className="recent-section"><h2>{t("home.recent")}</h2>{results.length ? <div className="recent-grid" data-clarity-mask="true">{results.map((result) => <a className="recent-file" href={result.url} download={result.name} key={result.id}><span className="file-glyph">PDF</span><span><strong>{result.name}</strong><small>{new Date(result.createdAt).toLocaleString()}</small></span><ArrowDownTrayIcon /></a>)}</div> : <div className="empty-inline"><FolderIcon /><span>{t("home.noRecent")}</span></div>}</section>
  </div><RightRail /></Shell>;
}

function downloadBlob(blob: Blob, name: string): string {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  return url;
}

function readFileArrayBuffer(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("File read failed."));
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.readAsArrayBuffer(file);
  });
}

function localInputMime(file: File): string {
  return documentMimeForFile(file) ?? file.type;
}

const fallbackConversionPairs = [
  { inputMimeTypes: ["application/pdf"], target: "text", outputMimeType: "text/plain", executionModes: ["local"] },
  { inputMimeTypes: ["application/pdf"], target: "markdown", outputMimeType: "text/markdown", executionModes: ["local"] },
  { inputMimeTypes: ["application/pdf"], target: "html", outputMimeType: "text/html", executionModes: ["local"] },
  { inputMimeTypes: ["application/pdf"], target: "json", outputMimeType: "application/json", executionModes: ["local"] },
  { inputMimeTypes: ["application/pdf"], target: "xml", outputMimeType: "application/xml", executionModes: ["local"] },
  { inputMimeTypes: ["application/pdf"], target: "csv", outputMimeType: "text/csv", executionModes: ["local"] },
  { inputMimeTypes: ["image/png", "image/jpeg"], target: "pdf", outputMimeType: "application/pdf", executionModes: ["local"] },
] as const satisfies NonNullable<DocumentCapability["conversionPairs"]>;

const extensionsByMime: Readonly<Record<string, readonly string[]>> = {
  "application/pdf": [".pdf"], "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/vnd.ms-excel": [".xls"], "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-powerpoint": [".ppt"], "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
  "application/vnd.oasis.opendocument.text": [".odt"], "application/vnd.oasis.opendocument.spreadsheet": [".ods"],
  "application/vnd.oasis.opendocument.presentation": [".odp"], "application/rtf": [".rtf"], "text/plain": [".txt"],
  "text/html": [".html", ".htm"], "application/epub+zip": [".epub"], "application/vnd.amazon.ebook": [".azw3"],
  "application/x-mobipocket-ebook": [".mobi"], "application/x-fictionbook+xml": [".fb2"], "application/vnd.comicbook+zip": [".cbz"],
  "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"],
};

function acceptValue(mimeTypes: readonly string[]): string {
  const uniqueMimes = [...new Set(mimeTypes)];
  return [...uniqueMimes, ...uniqueMimes.flatMap((mime) => extensionsByMime[mime] ?? [])].join(",");
}

const conversionTargetLabels: Readonly<Record<ConversionTarget, string>> = {
  pdf: "PDF", text: "TXT", markdown: "Markdown", html: "HTML", json: "JSON", xml: "XML", csv: "CSV", epub: "EPUB", azw3: "AZW3",
};

function ToolWorkspace({ operation }: { operation: DocumentOperation }) {
  const { t } = useTranslation();
  const tool = toolByOperation(operation);
  const Icon = tool.icon;
  const storedFiles = useDocumentStore((state) => state.pendingFiles);
  const storedCloudAssets = useDocumentStore((state) => state.pendingCloudAssets);
  const setStoredFiles = useDocumentStore((state) => state.setPendingFiles);
  const setStoredCloudAssets = useDocumentStore((state) => state.setPendingCloudAssets);
  const addRecentResult = useDocumentStore((state) => state.addRecentResult);
  const [files, setFiles] = useState<File[]>(storedFiles);
  const [status, setStatus] = useState<"idle" | "processing" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<{ name: string; url: string } | null>(null);
  const [ranges, setRanges] = useState("1");
  const [splitMode, setSplitMode] = useState<SplitMode>("ranges");
  const [pagesPerOutput, setPagesPerOutput] = useState(1);
  const [format, setFormat] = useState<"png" | "jpeg">("png");
  const [dpi, setDpi] = useState(144);
  const [compressionMode, setCompressionMode] = useState<"lossless" | "balanced">("lossless");
  const [quality, setQuality] = useState(0.78);
  const [ocrMode, setOcrMode] = useState<"missing_text" | "all">("missing_text");
  const [ocrLanguages, setOcrLanguages] = useState<Array<"zh-CN" | "zh-TW" | "en" | "ja">>(["zh-CN", "en"]);
  const [conversionTarget, setConversionTarget] = useState<ConversionTarget>("text");
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [fileInspectionError, setFileInspectionError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const capabilities = useQuery({
    queryKey: ["document-capabilities"],
    queryFn: () => documentApiClient.getCapabilities(),
    staleTime: 60_000,
  });
  const advertisedConversionPairs = capabilities.data?.find((item) => item.operation === "convert")?.conversionPairs;
  const conversionPairs = advertisedConversionPairs?.length ? advertisedConversionPairs : fallbackConversionPairs;
  const conversionTargets = [...new Set(conversionPairs.map((pair) => pair.target))];
  const selectedConversionPairs = conversionPairs.filter((pair) => pair.target === conversionTarget);
  const conversionInputMimes = [...new Set(selectedConversionPairs.flatMap((pair) => pair.inputMimeTypes))];

  useEffect(() => {
    if (operation === "convert" && !conversionTargets.includes(conversionTarget) && conversionTargets[0]) {
      setConversionTarget(conversionTargets[0]);
    }
  }, [conversionTarget, conversionTargets, operation]);

  useEffect(() => { setStoredFiles(files); }, [files, setStoredFiles]);
  useEffect(() => () => { if (result?.url.startsWith("blob:")) URL.revokeObjectURL(result.url); }, [result]);
  useEffect(() => {
    if (!files[0] || (operation !== "split" && operation !== "pdf_to_images")) {
      setPageCount(null);
      setFileInspectionError(false);
      return;
    }
    let cancelled = false;
    setPageCount(null);
    setFileInspectionError(false);
    void readFileArrayBuffer(files[0])
      .then((buffer) => getPdfPageCount(new Uint8Array(buffer)))
      .then((count) => { if (!cancelled) setPageCount(count); })
      .catch(() => { if (!cancelled) setFileInspectionError(true); });
    return () => { cancelled = true; };
  }, [files, operation]);
  useEffect(() => {
    setStatus((current) => current === "processing" ? current : "idle");
  }, [dpi, format, pagesPerOutput, quality, ranges, splitMode]);

  const convertsImagesToPdf = operation === "convert" && conversionTarget === "pdf" && selectedConversionPairs.some((pair) =>
    pair.executionModes.includes("local") && pair.inputMimeTypes.some((mime) => mime.startsWith("image/"))
  );
  const inputAccept = operation === "convert" ? acceptValue(conversionInputMimes) : "application/pdf,.pdf";
  const inputMultiple = operation === "merge" || convertsImagesToPdf;
  const chooseLabel = operation === "convert" ? t("workspace.chooseConvert") : t("workspace.choose");
  const emptyLabel = operation === "convert" ? t(convertsImagesToPdf ? "workspace.emptyImages" : "workspace.emptyConvert") : t("workspace.empty");
  const acceptsFile = (file: File) => operation === "convert"
    ? conversionInputMimes.includes(localInputMime(file))
    : localInputMime(file) === "application/pdf";
  const addFiles = (selected: FileList | null) => {
    if (!selected) return;
    const accepted = Array.from(selected).filter(acceptsFile);
    setFiles((current) => inputMultiple ? [...current, ...accepted] : accepted.slice(0, 1));
    setStatus("idle");
  };
  const changeConversionTarget = (target: ConversionTarget) => {
    setConversionTarget(target);
    setFiles([]);
    setStoredCloudAssets([]);
    setStatus("idle");
    setResult(null);
  };
  const move = (index: number, direction: -1 | 1) => setFiles((current) => {
    const target = index + direction;
    if (target < 0 || target >= current.length) return current;
    const next = [...current];
    [next[index], next[target]] = [next[target]!, next[index]!];
    return next;
  });
  const drop = (event: DragEvent<HTMLDivElement>) => { event.preventDefault(); addFiles(event.dataTransfer.files); };
  const requiredCount = operation === "merge" ? 2 : 1;
  const cloudAssets = storedCloudAssets;
  const optionError = useMemo(() => {
    if (fileInspectionError) return t("workspace.invalidPdf");
    if ((operation === "split" || operation === "pdf_to_images") && files.length > 0 && pageCount === null) return t("workspace.inspectingPdf");
    if (pageCount !== null) {
      try {
        if (operation === "split") buildSplitPageGroups(splitMode, ranges, pagesPerOutput, pageCount);
        if (operation === "pdf_to_images") parsePageRanges(ranges, pageCount);
      } catch {
        return t("workspace.invalidPages", { count: pageCount });
      }
    }
    if (operation === "pdf_to_images" && format === "jpeg") {
      try { validateImageQuality(quality); } catch { return t("workspace.invalidQuality"); }
    }
    return "";
  }, [fileInspectionError, files.length, format, operation, pageCount, pagesPerOutput, quality, ranges, splitMode, t]);
  const localConversionCompatible = operation !== "convert" || selectedConversionPairs.some((pair) =>
    pair.executionModes.includes("local") && files.every((file) => pair.inputMimeTypes.includes(localInputMime(file)))
  );
  const canRun = files.length >= requiredCount && tool.local && localConversionCompatible && status !== "processing" && !optionError;
  const cloudSettings: CloudJobSettings = { ranges, splitMode, pagesPerOutput, format, dpi, compressionMode, quality, ocrMode, ocrLanguages, conversionTarget };
  const recordCloudResult = useCallback((cloudResult: { id: string; name: string; url: string; createdAt: number }) => {
    setResult({ name: cloudResult.name, url: cloudResult.url });
    addRecentResult(cloudResult);
  }, [addRecentResult]);

  const run = async () => {
    if (files.length < requiredCount) { setStatus("error"); setMessage(t("workspace.filesRequired")); return; }
    if (!tool.local) return;
    setStatus("processing");
    setMessage("");
    try {
      const bytes = await Promise.all(files.map(async (file) => new Uint8Array(await readFileArrayBuffer(file))));
      const { blob, name } = await processLocalPdf(operation, bytes, { ranges, format, dpi, quality, splitMode, pagesPerOutput, conversionTarget }, files.map(localInputMime));
      const url = downloadBlob(blob, name);
      setResult({ name, url });
      addRecentResult({ id: crypto.randomUUID(), name, url, createdAt: Date.now() });
      setStatus("success");
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : t("workspace.error"));
    }
  };

  return <Shell rail={false}><div className="workspace-page">
    <Link className="back-link" to="/"><ArrowLeftIcon />{t("workspace.back")}</Link>
    <header className="workspace-header"><span className={`tool-icon large ${tool.tone}`}><Icon /></span><div><span className="workspace-mode">{t(tool.local ? "common.local" : "common.cloud")} {tool.local ? "" : `· ${t("common.beta")}`}</span><h1>{t(`tools.${operation}.name`)}</h1><p>{t(`tools.${operation}.description`)}</p></div></header>
    <div className="workspace-grid">
      <section className="file-panel"><div className="panel-heading"><div><h2>{t("workspace.selected")}</h2><span>{files.length + cloudAssets.length}</span></div><button className="secondary-button" type="button" onClick={() => inputRef.current?.click()}>{t("workspace.add")}</button></div>
        <input ref={inputRef} className="sr-only" aria-label={chooseLabel} type="file" accept={inputAccept} multiple={inputMultiple} onChange={(event) => addFiles(event.target.files)} />
        {files.length || cloudAssets.length ? <ul className="file-list" data-clarity-mask="true">{cloudAssets.map((asset) => <li key={asset.assetId}><span className="file-glyph cloud">PDF</span><span className="file-data"><strong>{asset.name}</strong><small>{t("workspace.savedCloudFile")} · {(asset.bytes / 1024 / 1024).toFixed(2)} MB</small></span><IconButton label={t("workspace.remove")} onClick={() => setStoredCloudAssets(storedCloudAssets.filter((item) => item.assetId !== asset.assetId))}><TrashIcon /></IconButton></li>)}{files.map((file, index) => <li key={`${file.name}-${file.lastModified}-${index}`}><span className="file-glyph">{file.type.startsWith("image/") ? "IMG" : "PDF"}</span><span className="file-data"><strong>{file.name}</strong><small>{(file.size / 1024 / 1024).toFixed(2)} MB</small></span>{(operation === "merge" || convertsImagesToPdf) && <><IconButton label={t("workspace.moveUp")} disabled={index === 0} onClick={() => move(index, -1)}><ArrowLeftIcon className="rotate-up" /></IconButton><IconButton label={t("workspace.moveDown")} disabled={index === files.length - 1} onClick={() => move(index, 1)}><ArrowLeftIcon className="rotate-down" /></IconButton></>}<IconButton label={t("workspace.remove")} onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}><TrashIcon /></IconButton></li>)}</ul> : <div className="workspace-dropzone" onDragOver={(event) => event.preventDefault()} onDrop={drop}><CloudArrowUpIcon /><strong>{emptyLabel}</strong><button type="button" className="primary-outline" onClick={() => inputRef.current?.click()}>{chooseLabel}</button></div>}
      </section>
      <aside className="options-panel"><h2>{t("workspace.options")}</h2>
        {operation === "split" && <><label><span>{t("workspace.splitMode")}</span><select value={splitMode} onChange={(event) => setSplitMode(event.target.value as SplitMode)}><option value="ranges">{t("workspace.splitRanges")}</option><option value="every">{t("workspace.splitEvery")}</option><option value="selected">{t("workspace.splitSelected")}</option></select></label>{splitMode === "every" ? <label><span>{t("workspace.every")}</span><input type="number" min="1" step="1" value={pagesPerOutput} onChange={(event) => setPagesPerOutput(Number(event.target.value))} /></label> : <label><span>{t(splitMode === "selected" ? "workspace.selectedPages" : "workspace.ranges")}</span><input value={ranges} onChange={(event) => setRanges(event.target.value)} placeholder="1,3-5" /></label>}</>}
        {operation === "pdf_to_images" && <><label><span>{t("workspace.ranges")}</span><input value={ranges} onChange={(event) => setRanges(event.target.value)} placeholder="1,3-5" /></label><label><span>{t("workspace.format")}</span><select value={format} onChange={(event) => setFormat(event.target.value as "png" | "jpeg")}><option value="png">PNG</option><option value="jpeg">JPEG</option></select></label><label><span>{t("workspace.dpi")}</span><input type="number" min="72" max="300" step="1" value={dpi} onChange={(event) => setDpi(Number(event.target.value))} /></label>{format === "jpeg" ? <label><span>{t("workspace.quality")}</span><input type="number" min="0.1" max="1" step="0.01" value={quality} onChange={(event) => setQuality(Number(event.target.value))} /></label> : null}</>}
        {operation === "convert" && <label><span>{t("workspace.conversionTarget")}</span><select value={conversionTarget} onChange={(event) => changeConversionTarget(event.target.value as ConversionTarget)}>{conversionTargets.map((target) => <option value={target} key={target}>{conversionTargetLabels[target]}{target === "pdf" && convertsImagesToPdf && selectedConversionPairs.every((pair) => pair.executionModes.includes("local")) ? ` (${t("workspace.fromImages")})` : ""}</option>)}</select></label>}
        {operation === "compress" && <><label><span>{t("workspace.compressionMode")}</span><select value={compressionMode} onChange={(event) => setCompressionMode(event.target.value as "lossless" | "balanced")}><option value="lossless">{t("workspace.lossless")}</option><option value="balanced">{t("workspace.balanced")}</option></select></label>{compressionMode === "balanced" ? <><label><span>{t("workspace.dpi")}</span><input type="number" min="72" max="300" step="1" value={dpi} onChange={(event) => setDpi(Number(event.target.value))} /></label><label><span>{t("workspace.quality")}</span><input type="number" min="0.1" max="1" step="0.01" value={quality} onChange={(event) => setQuality(Number(event.target.value))} /></label></> : null}</>}
        {operation === "ocr" && <><label><span>{t("workspace.ocrMode")}</span><select value={ocrMode} onChange={(event) => setOcrMode(event.target.value as "missing_text" | "all")}><option value="missing_text">{t("workspace.missingText")}</option><option value="all">{t("workspace.allPages")}</option></select></label><fieldset className="language-options"><legend>{t("workspace.languages")}</legend>{(["zh-CN", "zh-TW", "en", "ja"] as const).map((language) => <label key={language}><input type="checkbox" checked={ocrLanguages.includes(language)} onChange={(event) => setOcrLanguages((current) => event.target.checked ? [...current, language] : current.filter((item) => item !== language))} /><span>{localeLabels[language]}</span></label>)}</fieldset></>}
        {operation === "pdf_to_docx" && <p className="fidelity-note">{t("workspace.fidelity")}</p>}
        {optionError ? <p className={`status-message ${fileInspectionError ? "error" : ""}`} role={fileInspectionError ? "alert" : "status"}>{optionError}</p> : pageCount !== null ? <p className="status-message" role="status">{t("workspace.pageCount", { count: pageCount })}</p> : null}
        {tool.local ? <><p className="privacy-note"><ShieldCheckIcon />{t("workspace.localNote")}</p><button className="primary-button" type="button" disabled={!canRun} onClick={() => void run()}>{status === "processing" ? t("workspace.processing") : t("workspace.run")}</button></> : null}
        <CloudExecution operation={operation} files={files} existingAssetIds={cloudAssets.map((asset) => asset.assetId)} existingAssetMimes={cloudAssets.map((asset) => asset.mime)} inputBytes={files.reduce((sum, file) => sum + file.size, cloudAssets.reduce((sum, asset) => sum + asset.bytes, 0))} requiredCount={requiredCount} settings={cloudSettings} hideWhenUnavailable={tool.local} onResult={recordCloudResult} />
        {status === "error" && <p className="status-message error" role="alert">{message || t("workspace.error")}</p>}
        {status === "success" && result && <div className="result-card" role="status"><strong>{t("workspace.success")}</strong><a href={result.url} download={result.name}><ArrowDownTrayIcon />{t("workspace.download")}</a></div>}
      </aside>
    </div>
  </div></Shell>;
}

const pdfToolOperations = ["merge", "compress", "split", "pdf_to_docx", "pdf_to_images", "ocr"] as const satisfies readonly DocumentOperation[];

function CatalogToolCard({ operation, capability, pending }: { operation: DocumentOperation; capability: DocumentCapability | undefined; pending: boolean }) {
  const { t } = useTranslation();
  const tool = toolByOperation(operation);
  const Icon = tool.icon;
  const local = capability ? capability.executionModes.includes("local") : tool.local;
  const server = capability?.executionModes.includes("server") ?? false;
  const unavailable = !pending && !local && !server;
  const mode = pending && !local
    ? t("catalog.checking")
    : unavailable
      ? t("catalog.unavailable")
      : local && server
        ? t("catalog.localAndServer")
        : local
          ? t("catalog.localOnly")
          : t("catalog.serverAvailable");
  const content = <>
    <span className={`tool-icon ${tool.tone}`}><Icon /></span>
    <strong>{t(`tools.${operation}.name`)}</strong>
    <span>{t(`tools.${operation}.description`)}</span>
    <em className={`catalog-status ${unavailable ? "unavailable" : local ? "local" : "cloud"}`}>{mode}</em>
  </>;

  return unavailable
    ? <div data-testid={`catalog-tool-${operation}`} className="quick-card catalog-tool disabled" aria-disabled="true">{content}</div>
    : <Link data-testid={`catalog-tool-${operation}`} className="quick-card catalog-tool" to={tool.path}>{content}</Link>;
}

function CatalogPage() {
  const { t } = useTranslation();
  const capabilities = useQuery({
    queryKey: ["document-capabilities"],
    queryFn: () => documentApiClient.getCapabilities(),
    staleTime: 60_000,
  });
  const capabilityFor = (operation: DocumentOperation) => capabilities.data?.find((item) => item.operation === operation);
  const conversionCapability = capabilityFor("convert");
  const conversionPairs = conversionCapability?.conversionPairs?.length ? conversionCapability.conversionPairs : fallbackConversionPairs;
  return <Shell rail={false}><div className="catalog-page">
    <div className="section-heading"><div><h1>{t("pages.allTitle")}</h1><p>{t("pages.allSubtitle")}</p></div></div>
    <section className="catalog-section" aria-labelledby="pdf-tools-heading">
      <h2 id="pdf-tools-heading">{t("catalog.pdfTools")}</h2>
      <div className="catalog-grid">{pdfToolOperations.map((operation) => <CatalogToolCard operation={operation} capability={capabilityFor(operation)} pending={capabilities.isPending} key={operation} />)}</div>
    </section>
    <section className="catalog-section" aria-labelledby="conversion-heading">
      <h2 id="conversion-heading">{t("catalog.conversionCenter")}</h2>
      <div className="conversion-catalog">
        <CatalogToolCard operation="convert" capability={conversionCapability} pending={capabilities.isPending} />
        <div className="conversion-pairs" aria-label={t("catalog.availableConversions")}>
          {[...new Set(conversionPairs.map((pair) => pair.target))].map((target) => {
            const pairs = conversionPairs.filter((pair) => pair.target === target);
            const modes = [...new Set(pairs.flatMap((pair) => pair.executionModes))];
            const mode = modes.includes("local") && modes.includes("server") ? t("catalog.localAndServer") : modes.includes("local") ? t("catalog.localOnly") : t("catalog.serverAvailable");
            return <div className="conversion-pair" key={target}><span>{t("catalog.convertTo", { target: conversionTargetLabels[target] })}</span><em>{mode}</em></div>;
          })}
        </div>
      </div>
    </section>
  </div></Shell>;
}

function ToolRoute() {
  const { slug = "" } = useParams();
  const operation = useMemo(() => toolCatalog.find((tool) => tool.path.endsWith(slug))?.operation, [slug]);
  return operation ? <ToolWorkspace operation={operation} /> : <CatalogPage />;
}

export function AppRoutes() {
  return <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/tools" element={<CatalogPage />} />
    <Route path="/tools/:slug" element={<ToolRoute />} />
    <Route path="/files" element={<Shell rail={false}><CloudFilesContent /></Shell>} />
    <Route path="/activity" element={<Shell rail={false}><CloudActivityContent /></Shell>} />
    <Route path="/signin" element={<Shell rail={false} mobileNav={false}><SignInContent /></Shell>} />
    <Route path="/auth/callback" element={<Shell rail={false} mobileNav={false}><AuthCallbackContent /></Shell>} />
    <Route path="/auth/email" element={<Shell rail={false} mobileNav={false}><AuthEmailContent /></Shell>} />
    <Route path="*" element={<HomePage />} />
  </Routes>;
}

export default function App() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  const { i18n, t } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.title = t("seo.title");
    document.querySelector('meta[name="description"]')?.setAttribute("content", t("seo.description"));
  }, [i18n.language, t]);
  return <AppRoutes />;
}
